import { Controller } from '@hotwired/stimulus'

// "Request a call" modal. The header buttons open a native <dialog>; the form is
// submitted via fetch so the user stays on the current page, and the server's
// HTML response (success message or the form with errors) is swapped into the
// body. The submit button is gated on the consent checkbox (server still enforces).
export default class extends Controller<HTMLElement> {
  static targets = ['dialog', 'body']

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly bodyTarget: HTMLElement

  private initialBody = ''

  connect(): void {
    this.initialBody = this.bodyTarget.innerHTML
    this.gateSubmit()
  }

  open(event: Event): void {
    event.preventDefault()
    this.bodyTarget.innerHTML = this.initialBody
    this.gateSubmit()
    this.dialogTarget.showModal()
  }

  close(): void {
    this.dialogTarget.close()
  }

  // Close when the backdrop (the dialog element itself) is clicked.
  backdrop(event: MouseEvent): void {
    if (event.target === this.dialogTarget) this.dialogTarget.close()
  }

  consentChanged(): void {
    this.gateSubmit()
  }

  async submit(event: SubmitEvent): Promise<void> {
    event.preventDefault()
    const form = event.target as HTMLFormElement
    const button = form.querySelector<HTMLButtonElement>('[type=submit]')
    if (button) button.disabled = true

    let html: string
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      })
      html = await response.text()
    } catch {
      if (button) button.disabled = false
      return
    }

    this.bodyTarget.innerHTML = html
    this.gateSubmit()
  }

  // Disable the submit button until consent is checked. Runs after each body swap
  // since the elements are replaced.
  private gateSubmit(): void {
    const consent = this.bodyTarget.querySelector<HTMLInputElement>('input[type=checkbox]')
    const button = this.bodyTarget.querySelector<HTMLButtonElement>('[type=submit]')
    if (button) button.disabled = !consent?.checked
  }
}
